/**
 * LiveVideoOverlay (web) — realtime Live Vision. Publishes the browser camera
 * into the active agent room (Gemini Live) and renders the local preview by
 * attaching the LiveKit video track to a <video> element. Mirrors the RN
 * MenuScanScreen realtime path. Gated by live_vision_realtime + a live voice
 * session; the publish itself is driven by useAudioChat.setLiveVideoEnabled.
 */
import { useEffect, useRef, useState } from 'react';
import Text from '../../common/Text';
import Icon from '../../common/Icon';
import { useLanguage } from '../../hooks/useLanguage';

interface Props {
  visible: boolean;
  onClose: () => void;
  /** Toggle the camera in the agent room; returns the local track for preview. */
  setLiveVideoEnabled: (enabled: boolean) => Promise<any | null>;
}

export default function LiveVideoOverlay({
  visible,
  onClose,
  setLiveVideoEnabled,
}: Props) {
  const { t } = useLanguage();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const trackRef = useRef<any>(null);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setStarting(true);
    (async () => {
      const track = await setLiveVideoEnabled(true);
      if (cancelled) {
        try {
          await setLiveVideoEnabled(false);
        } catch {
          /* noop */
        }
        return;
      }
      trackRef.current = track;
      if (track && videoRef.current) {
        try {
          track.attach(videoRef.current);
        } catch {
          /* noop */
        }
      }
      setStarting(false);
    })();
    return () => {
      cancelled = true;
      try {
        trackRef.current?.detach?.();
      } catch {
        /* noop */
      }
      trackRef.current = null;
      // Stop publishing the camera when the overlay closes.
      setLiveVideoEnabled(false);
    };
  }, [visible, setLiveVideoEnabled]);

  if (!visible) return null;

  return (
    <div style={BACKDROP} role="dialog" aria-modal="true">
      <video ref={videoRef} autoPlay playsInline muted style={VIDEO} />
      <button
        type="button"
        onClick={onClose}
        style={CLOSE_BTN}
        aria-label={t('CLOSE', 'Close')}
      >
        <Icon name="close" size={24} color="#fff" />
      </button>
      <div style={PILL}>
        <span style={DOT} />
        <Text variant="body">
          {t('LIVE_WATCHING', 'Live · Leilani is watching')}
        </Text>
      </div>
      {starting && (
        <div style={STARTING}>
          <Text variant="body">
            {t('LIVE_VIDEO_STARTING', 'Starting live view…')}
          </Text>
        </div>
      )}
    </div>
  );
}

const BACKDROP: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 4000,
  background: '#000',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const VIDEO: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};
const CLOSE_BTN: React.CSSProperties = {
  position: 'absolute',
  top: 16,
  right: 16,
  width: 40,
  height: 40,
  borderRadius: 20,
  border: 'none',
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};
const PILL: React.CSSProperties = {
  position: 'absolute',
  bottom: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: 'rgba(0,0,0,0.55)',
  borderRadius: 16,
  padding: '8px 14px',
  border: '1px solid rgba(255,255,255,0.12)',
};
const DOT: React.CSSProperties = {
  width: 9,
  height: 9,
  borderRadius: 5,
  background: '#ff3b5c',
};
const STARTING: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
};
